import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  Box,
  Typography
} from '@mui/material';
import AppService from '../../../../Configurations/Appservice';

/**
 * Vertical Form Dialog
 * Add/Edit dialog for verticals
 * Uses mock data during UI-First phase
 */
const VerticalFormDialog = ({ open, onClose, onSave, vertical }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    nameAr: '',
    country: '',
    description: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);

  const isEdit = !!vertical;

  // Load countries from API when dialog opens
  useEffect(() => {
    if (open) {
      AppService.get('api/verticals/countries')
        .then((response) => {
          setCountries(response || []);
        })
        .catch((error) => {
          console.error('Failed to load countries', error);
        });
    }
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (vertical) {
        setFormData({
          code: vertical.code || '',
          name: vertical.name || '',
          nameAr: vertical.nameAr || '',
          country: vertical.country || '',
          description: vertical.description || '',
          isActive: vertical.isActive !== undefined ? vertical.isActive : true
        });
      } else {
        setFormData({
          code: '',
          name: '',
          nameAr: '',
          country: '',
          description: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [open, vertical]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z_]+$/.test(formData.code)) {
      newErrors.code = 'Code must be uppercase letters and underscores only';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit ? 'Edit Vertical' : 'Add New Vertical'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={handleChange('code')}
              error={!!errors.code}
              helperText={errors.code || 'e.g., UAE_PRO, SAUDI_PRO'}
              fullWidth
              required
              disabled={isEdit}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Country"
              value={formData.country}
              onChange={handleChange('country')}
              error={!!errors.country}
              helperText={errors.country}
              fullWidth
              required
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{country.flag}</span>
                    {country.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Name */}
          <Grid item xs={12}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name || 'Display name in English'}
              fullWidth
              required
            />
          </Grid>

          {/* Name Arabic */}
          <Grid item xs={12}>
            <TextField
              label="Name (Arabic)"
              value={formData.nameAr}
              onChange={handleChange('nameAr')}
              fullWidth
              dir="rtl"
              placeholder="الاسم بالعربية"
              helperText="Display name in Arabic (optional)"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={2}
              helperText="Brief description of this vertical"
            />
          </Grid>

          {/* Active Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange('isActive')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive verticals won't be available for document configuration
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerticalFormDialog;
